import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import stylesPc from "./EditAndUploadToHubModal.module.css";
import stylesSp from "./EditAndUploadToHubModal_SP.module.css";
import styleIpad from "./EditAndUploadToHubModal_IP.module.css";
import { IsDevice } from "../function/useDeviceFlag";
import { Modal, Button, createStyles, makeStyles, Theme, IconButton } from "@material-ui/core";
import { GetGroupList, GetUserInfoList } from "../../user/UserActions";
import { Group } from "../../user/User";
import { isAdmin, isEditor } from "../../utils/LogicUtils";
import { DeleteVideo, EditVideo, getTagList, ResetVideo } from "../../common/video/VideoActions";
import { UploadTimeline } from "../../studio/store/actions/TimelineActions";
import { TimelineInfo } from "../../studio/models/Timeline";
import { ExtractExtention, GetTagIdFromTagName, processLanguage } from "../../utils/ObjectUtils";
import PuiTypography from "../../components/elements/PuiTypography";
import ClearIcon from "@material-ui/icons/Clear";
import { VideoExtraMaterial } from "../../common/video/models/VideoExtraMaterial";
import OnLeaveDialog from "../../common/components/dialog/OnLeaveDialog";
import PuiFloatButton from "../../components/elements/PuiFloatButton";
import { AppState } from "../../root/RootReducer";
import ModalLowerInputItems from "./ModalLowerInputItems";
import ModalUpperInputItems from "./ModalUpperInputItems";
import { Video } from "../../common/video/models/Video";
import { t } from "i18next";

/**
|--------------------------------------------------
| タイムラインのcineonhubアップロード画面
| 動画編集画面
|--------------------------------------------------
*/

interface EditAndUploadToHubModalProps {
    // 編集したいビデオのid
    video?: Video;
    visibleModal?: boolean;
    showModal?: (visible: boolean) => void;
    timelineInfo?: TimelineInfo;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        editText: {
            width: "178px",
            height: "32px",
            paddingLeft: "20px",
            paddingTop: "3px",
            marginTop: "-16px",
            backgroundColor: "#1E2329",
            "&:hover": {
                backgroundColor: "#35393F",
            },
        },
        button: {
            fontFamily: "noto-sans-cjk-jp,sans-serif",
            fontSize: "14px",
            width: IsDevice("iphone") ? "120px" : "200px",
            height: "40px",
            "&:hover": {
                border: "none",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
        },
    })
);

function EditAndUploadToHubModal({
    video,
    visibleModal,
    showModal,
    timelineInfo,
}: EditAndUploadToHubModalProps) {
    // レスポンシブ対応：ブラウザサイズによるCSSファイルの切り替え
    const styles = IsDevice("desktop") ? stylesPc : IsDevice("ipad") ? styleIpad : stylesSp;

    const classes = useStyles();

    const history = useHistory();

    const dispatch = useDispatch();

    // ユーザー取得
    const { user, isSuccessDeleteVideo, tags } = useSelector((state: AppState) => ({
        user: state.user.user,
        isSuccessDeleteVideo: state.video.isSuccessDeleteVideo,
        tags: state.video.tags,
    }));

    const handlePropagation = (event: React.MouseEvent) => {
        event.nativeEvent.stopImmediatePropagation();
    };

    // モーダル表示・非表示フラグ
    const [isOpenVideoModal, OpenVideoModal] = useState(false);
    // 入力チェック
    const [isInputAllForm, CheckInputForm] = useState(false);

    // ユーザ入力保存確認
    const [isSaved, setIsSaved] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);

    // ビデオファイル
    const [inputVideoFile, InputVideoFile] = useState(null);
    const [textVideoFile, ChangeTextVideoFile] = useState("");

    // ビデオタイトル
    const [inputVideoName, InputVideoName] = useState(
        !video && timelineInfo ? timelineInfo.title : ""
    );

    // ビデオ要約
    const [inputVideoDetail, InputVideoDetail] = useState("");

    // プロフェッショナル
    const [inputVideoProfessional, InputVideoProfessional] = useState(!video ? "-1" : "");

    // グループ
    const [group, setGroup] = useState<Group[]>([]);

    // サムネイル画像ファイル
    // 表示状態 1= defaultUrl 2= imageText 3= imageThumbnail
    const [visiblePreviewImage, ChangePreviewImageFlag] = useState(!video ? 2 : 1);
    const [inputImageUrl, InputImageUrl] = useState("");
    const [inputImageFile, InputImageFile] = useState<File | null>(null);
    const [textImageFile, ChangeTextImageFile] = useState("");

    // タグ
    const [tagList, setTagList] = useState<string[]>([]);
    // タグ選択モーダル表示制御
    const [isOpenTagSelectModal, ShowTagSelectModal] = useState(false);

    // 資料アップロード
    const [alreadyUploadDocumentList, ChangeAlreadyUploadDocumentList] = useState<
        VideoExtraMaterial[]
    >([]);
    const [documentList, ChangeDocumentList] = useState<File[]>([]);
    const [documentNameList, ChangeDocumentNameList] = useState<string[]>([]);
    const [isErrorDocumentListLength, ErrorDocumentListLength] = useState(false);

    //アップロード動画の言語
    const [sttLanguage, setSttLanguage] = useState<string>("");
    //STT言語選択
    const [languageJa, setLanguageJa] = useState<boolean>(false);
    const [languageEn, setLanguageEn] = useState<boolean>(false);
    const [languageZh, setLanguageZh] = useState<boolean>(false);
    const [languageVi, setLanguageVi] = useState<boolean>(false);

    // アップロードモーダル表示時の初期表示
    useEffect(() => {
        if (!video && timelineInfo && visibleModal) {
            InputVideoName(timelineInfo.title);
            OpenVideoModal(visibleModal);
        }
        // eslint-disable-next-line
    }, [timelineInfo, visibleModal]);

    // APIコール
    useEffect(() => {
        dispatch(GetUserInfoList());
        dispatch(GetGroupList());
    }, [dispatch]);

    // ダイアログのOKボタン押下後、モデルを閉じる
    useEffect(() => {
        if (isSaved) {
            CloseVideoModal();
        }
        // eslint-disable-next-line
    }, [isSaved]);

    // ビデオ編集モーダル表示
    const OpenEditVideoModal = () => {
        if (!video) return;
        dispatch(getTagList([]));
        ChangeAlreadyUploadDocumentList(video.extraMaterials);
        InputVideoFile(null);
        ChangeTextVideoFile(video.fileName);
        ChangePreviewImageFlag(1);
        InputImageUrl(video.imageUrl);
        InputImageFile(null);
        InputVideoName(video.title);
        InputVideoDetail(video.description);
        InputVideoProfessional(video.presenterId);
        setTagList(video.tags);
        setGroup(video.groupList ?? []);
        OpenVideoModal(true);
        setSttLanguage(video.sttLanguage);
        setLanguageJa(video.languages.some((item) => item === "ja"));
        setLanguageEn(video.languages.some((item) => item === "en"));
        setLanguageZh(video.languages.some((item) => item === "zh-Hans"));
        setLanguageVi(video.languages.some((item) => item === "vi"));
        if (showModal) {
            showModal(true);
        }
    };

    // ビデオ編集モーダル非表示
    const CloseVideoModal = () => {
        if (isSaved) {
            OpenVideoModal(false);
            if (showModal) {
                showModal(false);
            }
            // リセット処理
            InputVideoFile(null);
            ChangeTextVideoFile("");
            ChangePreviewImageFlag(!video ? 2 : 1);
            InputImageUrl("");
            InputImageFile(null);
            ChangeTextImageFile("");
            InputVideoName("");
            InputVideoDetail("");
            ChangeAlreadyUploadDocumentList([]);
            ChangeDocumentList([]);
            ChangeDocumentNameList([]);
            InputVideoProfessional(!video ? "-1" : "");
            setGroup([]);
            setTagList([]);
            ShowTagSelectModal(false);
            ShowVerifyDeleteModal(false);
            CheckInputForm(false);
        } else {
            setOpenDialog(true);
        }
    };

    // cineonhubへアップロードボタン押下後、入力内容をバックエンドに送信
    const handleUpdateTimeline = () => {
        if (
            inputVideoName !== "" &&
            inputVideoName.length <= 80 &&
            inputVideoDetail !== "" &&
            inputVideoDetail.length <= 600 &&
            inputVideoProfessional !== "-1" &&
            inputVideoProfessional !== "" &&
            !isErrorDocumentListLength &&
            timelineInfo
        ) {
            dispatch(
                UploadTimeline({
                    imageFile: inputImageFile,
                    documentFile: documentList,
                    documentName: documentNameList,
                    timeLineInfo: {
                        id: timelineInfo.id,
                        filename: timelineInfo.name + ".mp4",
                        title: inputVideoName,
                        description: inputVideoDetail,
                        presenterId: Number(inputVideoProfessional),
                        tagList: tagList,
                        groupList: group.map((group) => Number(group.id)),
                        languages: processLanguage(languageJa, languageEn, languageZh, languageVi),
                    },
                })
            );
            if (showModal) {
                showModal(false);
            }
            setIsSaved(true);
        }
    };

    // ユーザキャンセルボタン押下
    const handleCancelButton = () => {
        CloseVideoModal();
    };

    // ユーザ編集完了ボタン押下
    const ClickConfirmButton = () => {
        if (visiblePreviewImage !== 1) {
            InputImageUrl("");
        }
        if (
            textVideoFile !== "Videoファイルを選択してください（1GBまで）" &&
            textVideoFile !== "ファイルを正しく選択してください（1GBまで）" &&
            inputVideoName !== "" &&
            inputVideoName.length <= 80 &&
            inputVideoDetail !== "" &&
            inputVideoDetail.length <= 600 &&
            !isErrorDocumentListLength
        ) {
            const defaultMaterialIdList = video?.extraMaterials?.map((materials) => materials.id);

            const materialIdList = alreadyUploadDocumentList?.map((materials) => materials.id);

            const sendMaterialIdList: number[] = defaultMaterialIdList
                ? defaultMaterialIdList.filter((i) => materialIdList.indexOf(i) === -1)
                : [];

            if (user && video) {
                dispatch(
                    EditVideo({
                        userId: user.id,
                        file: inputVideoFile,
                        imageFile: inputImageFile,
                        documentFile: documentList,
                        documentName: documentNameList,
                        materialId: sendMaterialIdList,
                        video: {
                            id: video.id,
                            title: inputVideoName,
                            description: inputVideoDetail,
                            imageUrl: inputImageUrl,
                            presenter: inputVideoProfessional,
                            tags: GetTagIdFromTagName(tagList, tags),
                            group: group.map((group) => Number(group.id)),
                            languages: processLanguage(languageJa, languageEn, languageZh, languageVi),
                        },
                    })
                );
                setIsSaved(true);
            } else {
                CheckInputForm(true);
            }
        }
    };

    // ビデオ削除後、一覧画面に戻る
    useEffect(() => {
        if (isSuccessDeleteVideo) {
            history.push("/videos");
            dispatch(ResetVideo());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccessDeleteVideo]);

    // ユーザ削除ボタン押下
    const ClickDeleteButton = () => {
        OpenVerifyDeleteModal();
    };

    // ビデオ削除確認モーダル表示制御
    const [isOpenVerifyDeleteModal, ShowVerifyDeleteModal] = useState(false);
    // ビデオ削除確認モーダル表示
    const OpenVerifyDeleteModal = () => {
        ShowVerifyDeleteModal(true);
    };
    // ビデオ削除確認モーダル非表示
    const CloseVerifyDeleteModal = () => {
        ShowVerifyDeleteModal(false);
    };
    // ユーザ削除確認ボタン押下
    const ClickConfirmDeleteUserButton = () => {
        if (video) {
            const type = ExtractExtention(video.fileName).replace(/^./, "");
            CloseVideoModal();
            CloseVerifyDeleteModal();
            dispatch(DeleteVideo(video.id, type));
        }
    };

    // ユーザ削除キャンセルボタン押下
    const ClickCancelDeleteUserButton = () => {
        CloseVerifyDeleteModal();
    };

    //　確認ダイアログを閉じる
    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    //　確認ダイアログのキャンセルボタン押下
    const handleCancelDialog = () => {
        handleCloseDialog();
    };

    //　確認ダイアログのページ移動ボタン押下
    const handleConfirmDialog = () => {
        setIsSaved(true);
        handleCloseDialog();
    };

    // アップロードボタンの有無効制御
    const [isReadyUploadTimeline, setReadyUploadTimeline] = useState<boolean>(false);

    // アップロードボタンの有無効制御
    useEffect(() => {
        if (timelineInfo && !video) {
            if (
                inputVideoName !== "" &&
                inputVideoName.length <= 80 &&
                inputVideoDetail !== "" &&
                inputVideoDetail.length <= 600 &&
                inputVideoProfessional !== "-1" &&
                inputVideoProfessional !== "" &&
                !isErrorDocumentListLength
            ) {
                setReadyUploadTimeline(true);
            } else {
                setReadyUploadTimeline(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputVideoName, inputVideoDetail, inputVideoProfessional, isErrorDocumentListLength]);

    return (
        <div>
            {(isAdmin(user?.roles) || isEditor(user?.roles)) && video && (
                <div className={styles.menuArea} onClick={handlePropagation}>
                    <div onClick={OpenEditVideoModal} className={classes.editText}>
                        <PuiTypography size={14}>{t("動画の編集")}</PuiTypography>
                    </div>
                </div>
            )}
            {/* Cineohubへアップロード・編集モーダル */}
            <Modal
                open={isOpenVideoModal}
                onClose={CloseVideoModal}
                className={styles.editAndUploadModal}
            >
                <div className={styles.modal}>
                    <IconButton onClick={handleCancelButton}>
                        <ClearIcon
                            style={{
                                marginTop: "5px",
                                color: "#808080",
                                fontSize: "29px",
                                top: "calc(2.5% - 13px)",
                                right: "calc(1% - 0px)",
                                position: "fixed",
                            }}
                        />
                    </IconButton>
                    <ul className={styles.inputForm}>
                        <form name="addVideoForm">
                            {!IsDevice("desktop") && (
                                <div onClick={CloseVideoModal} className={styles.closeButton}>
                                    <img
                                        src={
                                            require("../../../src/assets/image/closeFilter.svg")
                                                .default
                                        }
                                        alt="close"
                                    />
                                </div>
                            )}
                            <ul>
                                {video && (
                                    <div className={styles.titleText}>
                                        <PuiTypography size={18}>{t("動画の編集")}</PuiTypography>
                                    </div>
                                )}
                                {/* タイトル・プロフェッショナル・グループ・要約 */}
                                <ModalUpperInputItems
                                    isUploadDialog={video ? false : true}
                                    setIsSaved={setIsSaved}
                                    inputVideoName={inputVideoName}
                                    InputVideoName={InputVideoName}
                                    inputVideoProfessional={inputVideoProfessional}
                                    InputVideoProfessional={InputVideoProfessional}
                                    group={group}
                                    setGroup={setGroup}
                                    inputVideoDetail={inputVideoDetail}
                                    InputVideoDetail={InputVideoDetail}
                                />

                                {/* 画像サムネイルファイル */}
                                <ModalLowerInputItems
                                    setIsSaved={setIsSaved}
                                    isOpenVideoModal={isOpenVideoModal}
                                    // サムネイル
                                    inputImageUrl={inputImageUrl}
                                    setInputImageUrl={InputImageUrl}
                                    inputImageFile={inputImageFile}
                                    setInputImageFile={InputImageFile}
                                    visiblePreviewImage={visiblePreviewImage}
                                    ChangePreviewImageFlag={ChangePreviewImageFlag}
                                    textImageFile={textImageFile}
                                    ChangeTextImageFile={ChangeTextImageFile}
                                    // タグ
                                    tagList={tagList}
                                    setTagList={setTagList}
                                    isOpenTagSelectModal={isOpenTagSelectModal}
                                    ShowTagSelectModal={ShowTagSelectModal}
                                    // 資料
                                    alreadyUploadDocumentList={alreadyUploadDocumentList}
                                    ChangeAlreadyUploadDocumentList={
                                        ChangeAlreadyUploadDocumentList
                                    }
                                    documentList={documentList}
                                    ChangeDocumentList={ChangeDocumentList}
                                    documentNameList={documentNameList}
                                    ChangeDocumentNameList={ChangeDocumentNameList}
                                    ErrorDocumentListLength={ErrorDocumentListLength}
                                    //アップロード動画の言語
                                    sttLanguage={sttLanguage}
                                    // 言語選択
                                    languageJa={languageJa}
                                    setLanguageJa={setLanguageJa}
                                    languageEn={languageEn}
                                    setLanguageEn={setLanguageEn}
                                    languageZh={languageZh}
                                    setLanguageZh={setLanguageZh}
                                    languageVi={languageVi}
                                    setLanguageVi={setLanguageVi}
                                />

                                {/* アップロードボタン */}
                                {!video && (
                                    <div className={styles.buttonGroupForUpload}>
                                        {
                                            <PuiFloatButton
                                                type="submit"
                                                children={[t("cineonhubへアップロード")]}
                                                size="medium"
                                                onClick={handleUpdateTimeline}
                                                color="primary"
                                                isActive={isReadyUploadTimeline}
                                            />
                                        }
                                    </div>
                                )}
                            </ul>
                        </form>
                        {video && (
                            <li>
                                {/* 更新ボタン */}
                                <div className={styles.buttonGroupForEdit}>
                                    {isInputAllForm && (
                                        <div className={styles.checkInput}>
                                            {t("※全ての項目を正しく入力してください")}
                                        </div>
                                    )}
                                    <Button
                                        type="submit"
                                        onClick={ClickDeleteButton}
                                        style={{
                                            marginRight: "16px",
                                            fontSize: "16px",
                                            color: "#9C9C9C",
                                            fontFamily: "noto-sans-cjk-jp,sans-serif",
                                            border: "solid 2px #5D5D5D",
                                        }}
                                        className={styles.updateButton}
                                    >
                                        {t("削除")}
                                    </Button>
                                    {isSaved ? (
                                        <Button
                                            type="submit"
                                            disabled={isSaved}
                                            onClick={ClickConfirmButton}
                                            color="secondary"
                                            className={styles.updateButton}
                                            style={{
                                                color: "var(--loading-bg)",
                                                fontSize: "16px",
                                                fontFamily: "noto-sans-cjk-jp,sans-serif",
                                                backgroundColor: "var(--button-bg)",
                                            }}
                                        >
                                            {t("更新")}
                                        </Button>
                                    ) : (
                                        <Button
                                            type="submit"
                                            onClick={ClickConfirmButton}
                                            color="secondary"
                                            className={styles.updateButton}
                                            style={{
                                                color: "white",
                                                fontSize: "16px",
                                                fontWeight: "bold",
                                                fontFamily: "noto-sans-cjk-jp,sans-serif",
                                                backgroundColor: "var(--studio-btn-bg)",
                                            }}
                                        >
                                            {t("更新")}
                                        </Button>
                                    )}
                                </div>
                            </li>
                        )}
                    </ul>
                </div>
            </Modal>

            {/*------------------------ 動画削除モーダル ------------------------*/}
            <Modal open={isOpenVerifyDeleteModal} onClose={CloseVerifyDeleteModal}>
                <div className={styles.verifyDeleteModal}>
                    <Button onClick={CloseVerifyDeleteModal} />
                    <ul className={styles.deleteModalUl}>
                        <div className={styles.deleteModalTitle}>
                            {t("対象のVideoを削除しますか")}?
                        </div>
                        <li className={styles.deleteModalBtns}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                className={classes.button}
                                onClick={ClickCancelDeleteUserButton}
                                style={{
                                    marginRight: "10px",
                                    color: "#9C9C9C",
                                    fontFamily: "noto-sans-cjk-jp,sans-serif",
                                    border: "solid 2px #5D5D5D",
                                }}
                            >
                                {t("キャンセル")}
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                className={classes.button}
                                style={{ color: "white", backgroundColor: "var(--studio-btn-bg)" }}
                                onClick={ClickConfirmDeleteUserButton}
                            >
                                {t("削除")}
                            </Button>
                        </li>
                    </ul>
                </div>
            </Modal>

            {/*------------------------ ユーザ入力保存確認モーダル ------------------------*/}
            <OnLeaveDialog
                openDialog={openDialog}
                dialogTitle={t("このページを移動しますか？")}
                dialogContent={t("ページを移動すると、変更内容は失われます")}
                dialogActions={t("ページ移動")}
                handleCloseDialog={handleCloseDialog}
                handleCancel={handleCancelDialog}
                handleConfirm={handleConfirmDialog}
            />
        </div>
    );
}

export default EditAndUploadToHubModal;
